import { EventEmitter, Inject, Injectable } from '@angular/core';
import { APP_CONFIG, EnvironmentModel } from '@fh-client-interaction-frontends/fh-app-config';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, filter, finalize, first, map, Observable, of, tap, throwError } from 'rxjs';
import { ContractResumeResponseModel } from '../../helpers/objects/response/contract-resume/contract-resume-response-model';
import { CpContractModel } from '../../helpers/objects/dto/contract/cp-contract-model';
import { SharedEventsService } from '@fh-client-interaction-frontends/fh-providers';
import { CpInsuredPersonModel } from '../../helpers/objects/dto/insured-person/cp-insured-person-model';
import { CpCompanyContractModel } from '../../helpers/objects/dto/company-contract/cp-company-contract-model';
import { CpFacilitiesSetupModel } from '../../helpers/objects/dto/facilities/cp-facilities-setup-model';
import { CpPlansConsentsModal } from '@fh-client-interaction-frontends/fh-data-access-adhesion';

@Injectable({
  providedIn: 'root'
})
export class CpContractsService {
  appConfig: EnvironmentModel = new EnvironmentModel();

  listContracts: ContractResumeResponseModel[] | undefined;
  listInsuredPeopleToManage: CpInsuredPersonModel[] | undefined;
  contract: CpContractModel | undefined;
  onContractChange: EventEmitter<any> = new EventEmitter<any>();

  constructor(@Inject(APP_CONFIG) protected appConfigTemp: EnvironmentModel, private http: HttpClient, private sharedEventsService: SharedEventsService) {
    this.appConfig = appConfigTemp;
  }

  getAll(): Observable<ContractResumeResponseModel[]> {
    if (this.listContracts) {
      return of(this.listContracts);
    } else {
      return this.http.get<any>(this.appConfig.api + 'contracts').pipe(
        map((response) => response.body.Contracts as ContractResumeResponseModel[]),
        tap((response) => (this.listContracts = response)),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            // Clear session storage
            sessionStorage.clear();

            // Redirect to login
            window.location.href = '/login';
          }
          // Re-throw or return an empty observable depending on your needs
          return throwError(() => error);
        })
      );
    }
  }

  getCurrentContract(token?: string): Observable<CpContractModel> {
    if (token == undefined && this.contract?.Token) {
      return of(this.contract);
    } else {
      sessionStorage.setItem('contract', btoa(token!));
      return this.http.get<any>(this.appConfig.api + 'contracts/' + encodeURIComponent(token!)).pipe(
        map((response) => response.body as CpContractModel),
        tap((response) => {
          this.contract = response;
          if (this.contract.ContractNumber != '-') {
            this.getInsuredPeopleToManage(token!).subscribe();
          }
        })
      );
    }
  }

  getInsuredPeopleToManage(token?: string): Observable<CpInsuredPersonModel[]> {
    if (token == undefined && this.listInsuredPeopleToManage && this.listInsuredPeopleToManage.length > 0) {
      return of(this.listInsuredPeopleToManage);
    } else {
      if (token == undefined) {
        token = atob(sessionStorage.getItem('contract')!.toString());
      }

      return this.http.get<any>(this.appConfig.api + 'contracts/' + encodeURIComponent(token!) + '/insured-people-to-manage').pipe(
        map((response) => response.body.InsuredPersons as CpInsuredPersonModel[]),
        tap((response) => {
          this.listInsuredPeopleToManage = response;
          this.onContractChange.emit();
        })
      );
    }
  }

  getInsuredPeopleByCardNumber(cardNumber: string): Observable<CpInsuredPersonModel> {
    const token = atob(sessionStorage.getItem('contract')!.toString());

    return this.http
      .get<any>(this.appConfig.api + 'contracts/' + encodeURIComponent(token) + '/insured-persons/' + cardNumber)
      .pipe(map((response) => response.body.InsuredPerson as CpInsuredPersonModel));
  }

  updateInsuredPeopleByCardNumber(cardNumber: string, data: any): Observable<any> {
    const token = atob(sessionStorage.getItem('contract')!.toString());
    return this.http
      .put<any>(this.appConfig.api + 'contracts/' + encodeURIComponent(token) + '/insured-persons/' + cardNumber, data)
      .pipe(map((response) => response.body.InsuredPerson as CpInsuredPersonModel));
  }

  getVirtualCardImage(token: string, cardNumber: string): Observable<string> {
    return this.http
      .get<any>(this.appConfig.api + 'contracts/' + encodeURIComponent(token) + '/virtual-card-image/' + cardNumber)
      .pipe(map((response) => response.body.base64CardImage as string));
  }

  updateInsuredPersonIBAN(cardNumber: string, data: any): Observable<any> {
    const token = atob(sessionStorage.getItem('contract')!.toString());
    return this.http.put<any>(this.appConfig.api + 'contracts/' + encodeURIComponent(token) + '/insured-persons/' + cardNumber + '/iban', data);
  }

  deleteInsuredPerson(cardNumber: string): Observable<any> {
    const token = atob(sessionStorage.getItem('contract')!.toString());
    return this.http.delete<any>(this.appConfig.api + 'contracts/' + encodeURIComponent(token) + '/company-detail/insured-persons/' + cardNumber);
  }

  getCompanyContract(): Observable<CpCompanyContractModel> {
    const token = atob(sessionStorage.getItem('contract')!.toString());
    return this.http.get<any>(this.appConfig.api + 'contracts/' + encodeURIComponent(token) + '/company-detail').pipe(
      map((response) => {
        return response.body.company as CpCompanyContractModel;
      })
    );
  }

  updateContributiveData(data: any): Observable<any> {
    const token = atob(sessionStorage.getItem('contract')!.toString());
    return this.http
      .put<any>(this.appConfig.api + 'contracts/' + encodeURIComponent(token) + '/company-detail/contributives', data)
      .pipe(map((response) => response.body));
  }


  associateContract(data: { birthDate: string; token: string; country: string; cardNumber: string; nif: string }): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.append('X-Internal-SGHA', '1');
    return this.http.post<any>(this.appConfig.api + 'contracts', data, { headers }).pipe(
      tap(() => {
        this.listContracts = undefined;
      })
    );
  }

  getTableFieldsSetup(feature: string): Observable<CpFacilitiesSetupModel[]> {
    const token = atob(sessionStorage.getItem('contract')!.toString());

    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append('X-Context', feature);

    return this.http.get<any>(this.appConfig.api + 'tableFields/' + encodeURIComponent(token) + '/setup', {
      headers
    }).pipe(
      map(response => response.body.tableFieldsSetup as CpFacilitiesSetupModel[])
    );
  }

  getConsents(token: string): Observable<CpPlansConsentsModal[]> {
    return this.http.get<any>(this.appConfig.api + 'contracts/' + encodeURIComponent(token) + '/consents').pipe(
      map(response => {
        const consents = response.body || response;
        return (Array.isArray(consents) ? consents : []).map(
          (consent: any) => new CpPlansConsentsModal({
            id: consent.id,
            minAge: consent.minAge,
            requiredForSubscriptions: consent.requiredForSubscription || consent.requiredForSubscriptions || false,
            resume: consent.resume || '',
            htmlText: consent.htmlText || ''
          })
        );
      })
    );
  }

  updateConsents(token: string, name: string, consents: Array<{ consentTypeId: number; consentAccepted: boolean }>): Observable<any> {
    return this.http.put<any>(
      this.appConfig.api + 'contracts/' + encodeURIComponent(token) + '/consents/update',
      { name, consents }
    );
  }
}
